import type { ReactNode } from 'react';
import { Area, AreaChart, ResponsiveContainer, Tooltip, YAxis } from 'recharts';
import {
  AlertOctagon,
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  Cloud,
  CloudLightning,
  CloudRain,
  CloudSun,
  HelpCircle,
  Info,
  Sun,
  CircleCheck,
  CircleSlash,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { DailyPoint, DashboardAlert, Section, SeriesPoint, Severity, Weather, WeatherState } from '@/api/dashboard-live';
import { SEVERITY_COLOR, SEVERITY_LABEL, WEATHER_COLOR } from './theme';
import { ago, dateTime, dayLabel, dec, num, time } from './format';
import { usePilotage } from './context';

// ---------------------------------------------------------------- panneaux

export function Panel({
  title,
  subtitle,
  right,
  children,
  onOpen,
  className,
  bodyClassName,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  right?: ReactNode;
  children: ReactNode;
  onOpen?: () => void;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section
      className={cn(
        'flex min-h-0 flex-col rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900/70',
        onOpen && 'cursor-pointer transition-colors hover:border-slate-300 dark:hover:border-slate-600',
        className
      )}
      onClick={onOpen}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onKeyDown={onOpen ? (e) => (e.key === 'Enter' || e.key === ' ') && onOpen() : undefined}
    >
      <header className="flex items-start justify-between gap-2 px-4 pt-3">
        <div className="min-w-0">
          <h3 className="truncate text-[13px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{title}</h3>
          {subtitle && <p className="truncate text-xs text-slate-400 dark:text-slate-500">{subtitle}</p>}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {right}
          {onOpen && <Info className="h-3.5 w-3.5 text-slate-300 dark:text-slate-600" aria-hidden />}
        </div>
      </header>
      <div className={cn('min-h-0 flex-1 px-4 pb-3 pt-2', bodyClassName)}>{children}</div>
    </section>
  );
}

/** Pastille d'état d'une section (en échec, non configurée, réutilisée) */
export function SectionState({ section, label }: { section?: Section<unknown>; label?: string }) {
  const { now } = usePilotage();
  if (!section) return null;
  if (section.status === 'unconfigured') {
    return <span className="text-[11px] text-slate-400">non configuré</span>;
  }
  if (section.status === 'error') {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-red-600 dark:text-red-400" title={section.error}>
        <AlertTriangle className="h-3 w-3" aria-hidden />
        collecte en échec{section.lastSuccessAt ? ` · données ${ago(section.lastSuccessAt, now)}` : ''}
      </span>
    );
  }
  return (
    <span className="text-[11px] text-slate-400 dark:text-slate-500" title={`Collecté à ${time(section.collectedAt)}${section.durationMs ? ` en ${num(section.durationMs)} ms` : ''}`}>
      {label ?? ago(section.collectedAt, now)}
    </span>
  );
}

export function Unavailable({ section, what }: { section?: Section<unknown>; what: string }) {
  return (
    <div className="flex h-full min-h-16 flex-col items-center justify-center gap-1 text-center text-sm text-slate-400">
      <CircleSlash className="h-5 w-5" aria-hidden />
      <span>{section?.status === 'unconfigured' ? `${what} : source non configurée` : `${what} : pas de données`}</span>
      {section?.error && <span className="max-w-md truncate text-xs" title={section.error}>{section.error}</span>}
    </div>
  );
}

// ---------------------------------------------------------------- chiffres

/** Écart à une valeur de référence (hier) : flèche + valeur, sans couleur de statut */
export function Delta({ value, previous, unit = '', invert = false }: { value: number | null | undefined; previous: number | null | undefined; unit?: string; invert?: boolean }) {
  if (value === null || value === undefined || previous === null || previous === undefined || previous === 0) return null;
  const diff = value - previous;
  const rel = (100 * diff) / previous;
  if (Math.abs(rel) < 0.5) return <span className="text-xs text-slate-400">= hier</span>;
  const up = diff > 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  const good = invert ? !up : up;
  return (
    <span className={cn('inline-flex items-center gap-0.5 text-xs font-medium', good ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-500 dark:text-slate-400')}>
      <Icon className="h-3 w-3" aria-hidden />
      {up ? '+' : ''}
      {dec(rel, 0)} %{unit}
      <span className="font-normal text-slate-400"> vs hier</span>
    </span>
  );
}

export function BigNumber({ value, unit, className }: { value: ReactNode; unit?: string; className?: string }) {
  return (
    <div className={cn('flex items-baseline gap-1 font-semibold tabular-nums leading-none text-slate-900 dark:text-white', className)}>
      <span>{value}</span>
      {unit && <span className="text-[0.45em] font-medium text-slate-400">{unit}</span>}
    </div>
  );
}

/** Ligne « libellé ……… valeur » */
export function Row({ label, value, hint, swatch }: { label: ReactNode; value: ReactNode; hint?: ReactNode; swatch?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-0.5 text-sm">
      <span className="flex min-w-0 items-center gap-2 truncate text-slate-500 dark:text-slate-400">
        {swatch && <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: swatch }} aria-hidden />}
        {label}
      </span>
      <span className="shrink-0 tabular-nums font-medium text-slate-900 dark:text-slate-100">
        {value}
        {hint && <span className="ml-1 text-xs font-normal text-slate-400">{hint}</span>}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------- mini-graphiques

/** Sparkline (aire fine) avec info-bulle ; `points` journaliers ou fins */
export function Sparkline({
  points,
  color,
  height = 36,
  format = num,
  daily = false,
}: {
  points: (SeriesPoint | DailyPoint)[];
  color: string;
  height?: number;
  format?: (n: number) => string;
  daily?: boolean;
}) {
  const data = points.map((p) => ({ x: 'd' in p ? p.d : p.t, v: p.v }));
  if (data.length < 2) {
    return <div style={{ height }} className="flex items-end text-[11px] text-slate-400">historique en cours de constitution</div>;
  }
  const gid = `spark-${color.replace('#', '')}-${height}`;
  return (
    <div style={{ height }} onClick={(e) => e.stopPropagation()}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.28} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <YAxis hide domain={['dataMin', 'dataMax']} />
          <Tooltip
            cursor={{ stroke: color, strokeOpacity: 0.4 }}
            content={({ active, payload }) =>
              active && payload?.length ? (
                <div className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs shadow dark:border-slate-700 dark:bg-slate-800">
                  <div className="text-slate-400">{daily ? dayLabel(String(payload[0].payload.x)) : time(String(payload[0].payload.x))}</div>
                  <div className="font-medium tabular-nums text-slate-900 dark:text-white">{format(Number(payload[0].value))}</div>
                </div>
              ) : null
            }
          />
          <Area type="monotone" dataKey="v" stroke={color} strokeWidth={2} fill={`url(#${gid})`} isAnimationActive={false} dot={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Jauge horizontale 0-100 % */
export function Meter({ label, value, color, text }: { label: ReactNode; value: number | null | undefined; color: string; text?: ReactNode }) {
  const v = Math.max(0, Math.min(100, value ?? 0));
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between text-xs">
        <span className="text-slate-500 dark:text-slate-400">{label}</span>
        <span className="tabular-nums font-medium text-slate-900 dark:text-slate-100">{text ?? (value === null || value === undefined ? '–' : `${dec(value, 0)} %`)}</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
        <div className="h-full rounded-full transition-[width]" style={{ width: `${v}%`, background: color }} />
      </div>
    </div>
  );
}

/** Barre empilée horizontale (segments séparés de 2 px) + légende */
export function StackBar({ segments, height = 14 }: { segments: { key: string; label: string; value: number; color: string }[]; height?: number }) {
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  return (
    <div className="space-y-2">
      <div className="flex w-full gap-[2px] overflow-hidden rounded" style={{ height }}>
        {segments
          .filter((s) => s.value > 0)
          .map((s) => (
            <div key={s.key} title={`${s.label} : ${num(s.value)}`} style={{ width: `${(100 * s.value) / total}%`, background: s.color }} />
          ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {segments.map((s) => (
          <span key={s.key} className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: s.color }} aria-hidden />
            {s.label}
            <span className="font-medium tabular-nums text-slate-900 dark:text-slate-100">{num(s.value)}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- statuts

export function SeverityIcon({ severity, className }: { severity: Severity; className?: string }) {
  const Icon = severity === 'critical' ? AlertOctagon : severity === 'warning' ? AlertTriangle : Info;
  return <Icon className={cn('h-4 w-4 shrink-0', className)} style={{ color: SEVERITY_COLOR[severity] }} aria-label={SEVERITY_LABEL[severity]} />;
}

export function OkMark({ ok, label }: { ok: boolean; label?: string }) {
  return ok ? (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
      <CircleCheck className="h-3.5 w-3.5" aria-hidden />
      {label ?? 'OK'}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600 dark:text-red-400">
      <AlertOctagon className="h-3.5 w-3.5" aria-hidden />
      {label ?? 'KO'}
    </span>
  );
}

const WEATHER_ICON: Record<WeatherState, typeof Sun> = {
  sunny: Sun,
  cloudy: CloudSun,
  rainy: CloudRain,
  stormy: CloudLightning,
  unknown: HelpCircle,
};

export function WeatherIcon({ state, className }: { state: WeatherState; className?: string }) {
  const Icon = WEATHER_ICON[state] ?? Cloud;
  return <Icon className={className} style={{ color: WEATHER_COLOR[state] }} aria-hidden />;
}

/** Carte météo d'une plateforme */
export function WeatherCard({ weather, children, onOpen }: { weather: Weather; children?: ReactNode; onOpen: () => void }) {
  const top = weather.reasons.find((r) => r.severity !== 'info');
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group flex min-w-0 flex-col gap-2 rounded-xl border border-slate-200 bg-white p-3 text-left transition-colors hover:border-slate-300 dark:border-slate-800 dark:bg-slate-900/70 dark:hover:border-slate-600"
      style={{ boxShadow: `inset 0 3px 0 ${WEATHER_COLOR[weather.state]}` }}
    >
      <div className="flex items-center gap-3">
        <WeatherIcon state={weather.state} className="h-9 w-9 shrink-0" />
        <div className="min-w-0">
          <div className="text-base font-semibold text-slate-900 dark:text-white">{weather.label}</div>
          <div className="text-xs font-medium" style={{ color: WEATHER_COLOR[weather.state] }}>
            {weather.stateLabel}
            {weather.counts.critical + weather.counts.warning > 0 && (
              <span className="ml-1 text-slate-400">
                · {weather.counts.critical + weather.counts.warning} alerte{weather.counts.critical + weather.counts.warning > 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
      </div>
      {children && <div className="grid grid-cols-3 gap-2">{children}</div>}
      <div className="truncate text-xs text-slate-500 dark:text-slate-400" title={top?.title}>
        {top ? top.title : 'Aucune alerte'}
      </div>
    </button>
  );
}

export function MiniStat({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="truncate text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className="truncate text-sm font-semibold tabular-nums text-slate-900 dark:text-slate-100">{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------- tableaux (panneau de détail)

export function DataTable<T>({
  rows,
  columns,
  empty = 'Aucune ligne',
  max = 200,
}: {
  rows: T[];
  columns: { key: string; label: string; align?: 'right'; render: (row: T) => ReactNode }[];
  empty?: string;
  max?: number;
}) {
  if (!rows.length) return <p className="py-4 text-center text-sm text-slate-400">{empty}</p>;
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-800">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-800/60 dark:text-slate-400">
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={cn('px-3 py-2 font-medium', c.align === 'right' ? 'text-right' : 'text-left')}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {rows.slice(0, max).map((row, i) => (
            <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
              {columns.map((c) => (
                <td key={c.key} className={cn('px-3 py-1.5 text-slate-700 dark:text-slate-200', c.align === 'right' && 'text-right tabular-nums')}>
                  {c.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > max && <p className="px-3 py-2 text-xs text-slate-400">{num(rows.length - max)} lignes de plus non affichées</p>}
    </div>
  );
}

export function Explain({ children }: { children: ReactNode }) {
  return <div className="space-y-2 rounded-lg bg-slate-50 p-3 text-sm leading-relaxed text-slate-600 dark:bg-slate-800/60 dark:text-slate-300">{children}</div>;
}

export function AlertList({ alerts, empty = 'Aucune alerte ouverte', showResolved = false }: { alerts: DashboardAlert[]; empty?: string; showResolved?: boolean }) {
  if (!alerts.length) return <p className="py-6 text-center text-sm text-slate-400">{empty}</p>;
  return (
    <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 dark:divide-slate-800 dark:border-slate-800">
      {alerts.map((a) => (
        <li key={`${a.fingerprint}-${a.id ?? ''}`} className="flex gap-3 px-3 py-2">
          <SeverityIcon severity={a.severity} className="mt-0.5" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-slate-900 dark:text-slate-100">{a.title}</div>
            {a.detail && <div className="break-words text-xs text-slate-500 dark:text-slate-400">{a.detail}</div>}
            <div className="mt-0.5 text-[11px] text-slate-400">
              {SEVERITY_LABEL[a.severity]} · {a.category}
              {a.firstSeenAt && <> · depuis {dateTime(a.firstSeenAt)}</>}
              {a.occurrences && a.occurrences > 1 && <> · vue {num(a.occurrences)} fois</>}
              {showResolved && (a.resolvedAt ? <> · résolue {dateTime(a.resolvedAt)}</> : <> · <b>ouverte</b></>)}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

